class PredictiveSearch extends HTMLElement {
	/**
	 * Constructor to initialize the PredictiveSearch component.
	 */
	constructor() {
		super();

		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('#predictive-search');
		this.predictiveSearchDefault = this.querySelector('#predictive-search-default');
		const isMobile = window.innerWidth < 950;

		// Ajouter les attributs ARIA initiaux
		this.setupAccessibility();

		this.clearBtn = this.querySelector('.mm-search-clear');

		this.input.addEventListener(
			'input',
			this.debounce((event) => {
				this.onChange(event);
			}, 300).bind(this)
		);

		this.input.addEventListener('input', () => this.toggleClearButton());

		if (this.clearBtn) {
			this.clearBtn.addEventListener('click', () => {
				this.input.value = '';
				this.toggleClearButton();
				this.input.dispatchEvent(new Event('input', { bubbles: true }));
				this.input.focus();
			});
		}

		this.toggleClearButton();

		// Ouvrir la recherche prédictive au focus
		this.input.addEventListener('focus', () => {
			this.openOnFocus();
		});

		// Fermer la recherche prédictive au blur (avec délai pour permettre les clics)
		this.input.addEventListener('blur', (e) => {
			// Délai pour permettre les clics sur les résultats
			setTimeout(() => {
				// Vérifier si le focus n'est pas dans les résultats de recherche
				if (!this.contains(document.activeElement)) {
					this.closeSearch();
				}
			}, 200);
		});

		// Fermer la recherche si on clique sur le filtre
		const filter = document.querySelector('[data-filter]');
		if (filter) {
			filter.addEventListener('click', () => {
				if (this.predictiveSearchDefault?.style.display === 'flex' ||
					this.predictiveSearchResults?.style.display === 'block') {
					this.closeSearch();
					this.input.blur();
				}
			});
		}

		// Ajouter la gestion de la navigation au clavier
		this.setupKeyboardNavigation();
	}

	/**
	 * Configure les attributs d'accessibilité ARIA
	 */
	setupAccessibility() {
		// Attributs pour l'input de recherche
		this.input.setAttribute('aria-expanded', 'false');
		this.input.setAttribute('aria-controls', 'predictive-search');
		this.input.setAttribute('aria-autocomplete', 'list');
		this.input.setAttribute('role', 'combobox');

		// Attributs pour les résultats de recherche
		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.setAttribute('aria-hidden', 'true');
			this.predictiveSearchResults.setAttribute('role', 'listbox');
		}
	}

	/**
	 * Configure la navigation au clavier et les événements de souris
	 */
	setupKeyboardNavigation() {
		// Créer les handlers une seule fois
		this.documentClickHandler = (event) => {
			// Fermer si on clique en dehors de la recherche
			if (this.isOpen() && !this.contains(event.target)) {
				this.close();
			}
		};

		this.documentFocusHandler = (event) => {
			if (this.isOpen() && !this.contains(event.target)) {
				this.close();
			}
		};

		this.documentKeydownHandler = (event) => {
			if (event.key === 'Escape' && this.isOpen()) {
				this.close();
				this.input.blur();
			}
		};

		this.inputKeydownHandler = (event) => {
			if (event.key === 'Escape') {
				this.close();
				this.input.blur();
			}
		};

		this.resultsKeydownHandler = (event) => {
			if (event.key === 'Escape') {
				this.close();
				this.input.focus();
			}
		};

		// Ajouter les listeners
		document.addEventListener('click', this.documentClickHandler);
		document.addEventListener('focusin', this.documentFocusHandler);
		document.addEventListener('keydown', this.documentKeydownHandler);
		this.input.addEventListener('keydown', this.inputKeydownHandler);

		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.addEventListener('keydown', this.resultsKeydownHandler);
		}
	}

	/**
	 * Vérifie si le predictive search est ouvert
	 */
	isOpen() {
		return this.predictiveSearchResults && this.predictiveSearchResults.style.display === 'block';
	}

	/**
	 * Close the search (both default and results)
	 */
	closeSearch() {
		const header = document.querySelector(".mm-section-header");
		const closeIconActive = this.querySelector('.mm-icon-close-input-close-active');
		const isHomePage = document.body.classList.contains('template-index') ||
			document.body.classList.contains('index');

		// Cacher les deux panneaux
		if (this.predictiveSearchDefault) {
			this.predictiveSearchDefault.style.display = 'none';
		}
		if (this.predictiveSearchResults) {
			this.predictiveSearchResults.style.display = 'none';
		}

		// Toggle icons: hide close button
		if (closeIconActive) {
			closeIconActive.classList.remove('mm-active');
		}
		const closeButton = this.querySelector('.mm-search-close');
		if (closeButton) {
			closeButton.style.display = 'none';
		}

		// Retirer la classe mm-header-scroll (toutes pages)
		if (header) {
			header.classList.remove('mm-header-scroll');
		}

		// Sur les autres pages, re-cacher la search bar (override du style.display mis par le bouton)
		if (!isHomePage) {
			const headerSearch = document.querySelector('.js-header__search');
			if (headerSearch) headerSearch.style.display = '';
		}

		// Mettre à jour ARIA
		this.input.setAttribute('aria-expanded', 'false');

		// Vider l'input
		this.input.value = '';

		// Cacher le filtre et réactiver le scroll
		this.hideFilter();
		this.enableScroll();
	}

	/**
	 * Open default predictive search on focus
	 */
	openOnFocus() {
		const searchTerm = this.input.value.trim();
		const header = document.querySelector(".mm-section-header");
		const closeIconActive = this.querySelector('.mm-icon-close-input-close-active');

		// Ajouter la classe mm-header-scroll au header
		if (header && !header.classList.contains('mm-header-scroll')) {
			header.classList.add('mm-header-scroll');
		}

		// Update position based on input position
		if (typeof getPredictiveSearchPosition === 'function') {
			getPredictiveSearchPosition();
		}

		// Si l'input est vide, afficher la vue par défaut
		if (!searchTerm.length) {
			if (this.predictiveSearchDefault) {
				// Update position FIRST
				if (typeof getPredictiveSearchPosition === 'function') {
					getPredictiveSearchPosition();
				}

				// Then show elements (to preserve positioning)
				this.predictiveSearchDefault.style.display = 'flex';
				this.predictiveSearchResults.style.display = 'none';

				// Init articles Splide if not already done
				if (typeof mmInitSearchArticlesSplide === 'function') {
					mmInitSearchArticlesSplide();
				}

				// Show close button
				if (closeIconActive) {
					closeIconActive.classList.add('mm-active');
				}
				const closeButton = this.querySelector('.mm-search-close');
				if (closeButton) {
					closeButton.style.display = 'flex';
				}

				// Mettre à jour les attributs ARIA
				this.input.setAttribute('aria-expanded', 'true');

				// Activer le filtre et empêcher le scroll
				this.showFilter();
				this.preventScroll();
			}
		} else {
			// Si du texte est déjà présent, afficher la croix et lancer la recherche
			if (closeIconActive) {
				closeIconActive.classList.add('mm-active');
			}
			const closeButton = this.querySelector('.mm-search-close');
			if (closeButton) {
				closeButton.style.display = 'flex';
			}
			this.getSearchResults(searchTerm);
		}
	}

	/**
	 * Event handler for input changes.
	 */
	onChange() {
		const searchTerm = this.input.value.trim();

		if (!searchTerm.length) {
			// Afficher la vue par défaut au lieu de fermer
			if (this.predictiveSearchDefault) {
				this.predictiveSearchDefault.style.display = 'flex';
				this.predictiveSearchResults.style.display = 'none';

				// Update position AFTER display is set
				if (typeof getPredictiveSearchPosition === 'function') {
					getPredictiveSearchPosition();
				}

				this.input.setAttribute('aria-expanded', 'true');
				this.preventScroll();
			}
			return;
		}

		this.getSearchResults(searchTerm);
	}

	/**
	 * Fetch search results from the server based on the search term.
	 *
	 * @param {string} searchTerm - The term to search for.
	 */
	getSearchResults(searchTerm) {

    // fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)

		// Encoder le terme de recherche pour l'URL
		const encodedSearchTerm = encodeURIComponent(searchTerm);

		// Construire l'URL avec les paramètres pour inclure le contenu des articles
		// resources[articles][limit] permet de limiter le nombre d'articles retournés
		// Le contenu (body) des articles est recherché par défaut dans la recherche standard,
		// mais pour la recherche prédictive, cela dépend de la configuration Shopify
		const searchUrl = `${routes.predictive_search_url}?q=${encodedSearchTerm}&resources%5Btype%5D=product%2Ccollection%2Carticle&resources%5Blimit%5D=6&resources%5Boptions%5D%5Bfields%5D=title%2Cbody%2Ctag&section_id=predictive-search`;

		fetch(searchUrl)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}

				return response.text();
			})
			.then((text) => {
				try {
					const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
					const sectionElement = parsedDoc.querySelector('#shopify-section-predictive-search');

					if (sectionElement) {
						const resultsMarkup = sectionElement.innerHTML;
						this.predictiveSearchResults.innerHTML = resultsMarkup;
						this.open();
					} else {
						console.warn('Predictive search section not found in response');
						this.close();
					}
				} catch (parseError) {
					console.error('Error parsing predictive search response:', parseError);
					this.close();
				}
			})
			.catch((error) => {
				console.error('Predictive search error:', error);
				this.close();
			});
	}

	/**
	 * Open the predictive search results container.
	 */
	open() {
		if (!this.predictiveSearchResults || !this.predictiveSearchDefault) {
			console.warn('Predictive search elements not found');
			return;
		}

		this.predictiveSearchResults.style.display = 'block';
		this.predictiveSearchDefault.style.display = 'none';

		// Show close button
		const closeButton = this.querySelector('.mm-search-close');
		if (closeButton) closeButton.style.display = 'flex';

		// Update position AFTER display is set
		if (typeof getPredictiveSearchPosition === 'function') {
			getPredictiveSearchPosition();
		}

		// Mettre à jour les attributs ARIA
		this.input.setAttribute('aria-expanded', 'true');
		this.predictiveSearchResults.setAttribute('aria-hidden', 'false');

		// Only add stop-scrolling if search container is not already open
		const searchContainer = document.querySelector("[data-search-container]");
		if (!searchContainer || !searchContainer.classList.contains("mm-open")) {
			document.documentElement.classList.add("stop-scrolling");
		}

		this.showFilter();

		// Empêcher le scroll sur toutes les pages
		this.preventScroll();
	}

	showFilter() {
		const filterElement = document.querySelector("[data-filter]");
		if (filterElement) {
			filterElement.classList.add("active");
		}
	}

	hideFilter() {
		const filterElement = document.querySelector("[data-filter]");
		if (filterElement) {
			filterElement.classList.remove("active");
		}
	}

	/**
	 * Empêcher le scroll sur toutes les pages
	 */
	preventScroll() {
		if (typeof stopLenis === 'function') {
			try { stopLenis(); } catch (e) {}
		}
		document.documentElement.classList.add("stop-scrolling");
	}

	/**
	 * Réactiver le scroll sur toutes les pages
	 */
	enableScroll() {
		if (typeof startLenis === 'function') {
			try { startLenis(); } catch (e) {}
		}
		document.documentElement.classList.remove("stop-scrolling");
	}

	toggleClearButton() {
		if (!this.clearBtn) return;
		this.clearBtn.style.display = this.input.value.trim().length > 0 ? 'flex' : 'none';
	}

	// CLEAR SEARCH *****************************************************************************
	/*********************************************************************************************
	 * Clear the search input.
	 *
	 */
	mmClearSearch() {
		if (this.input) {
			this.input.value = "";
			// Trigger input event to update predictive search
			this.input.dispatchEvent(new Event('input', { bubbles: true }));
		}
	};

	/**
	 * Close the predictive search results container.
	 */
	close() {
		if (!this.predictiveSearchResults || !this.predictiveSearchDefault) {
			console.warn('Predictive search elements not found');
			return;
		}

		// Utiliser un délai pour éviter les conflits d'événements
		setTimeout(() => {
			this.predictiveSearchResults.style.display = 'none';
			this.predictiveSearchDefault.style.display = 'flex';

			// Update position AFTER display is set
			if (typeof getPredictiveSearchPosition === 'function') {
				getPredictiveSearchPosition();
			}

			// Mettre à jour les attributs ARIA
			this.input.setAttribute('aria-expanded', 'false');
			this.predictiveSearchResults.setAttribute('aria-hidden', 'true');

			// Only remove stop-scrolling if search container is not open
			const searchContainer = document.querySelector("[data-search-container]");
			if (!searchContainer || !searchContainer.classList.contains("mm-open")) {
				document.documentElement.classList.remove("stop-scrolling");
			}

			this.hideFilter();

			// Effacer automatiquement le contenu de l'input lors de la fermeture
			this.mmClearSearch();

			// Réactiver le scroll sur toutes les pages
			this.enableScroll();
		}, 10);
	}

	/**
	 * Debounce function to limit the rate at which a function can fire.
	 *
	 * @param {Function} fn - The function to debounce.
	 * @param {number} wait - The number of milliseconds to delay.
	 * @returns {Function} - The debounced function.
	 */
	debounce(fn, wait) {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, args), wait);
		};
	}

	/**
	 * Nettoyer les event listeners quand l'élément est détruit
	 */
	disconnectedCallback() {
		if (this.documentClickHandler) {
			document.removeEventListener('click', this.documentClickHandler);
		}
		if (this.documentFocusHandler) {
			document.removeEventListener('focusin', this.documentFocusHandler);
		}
		if (this.documentKeydownHandler) {
			document.removeEventListener('keydown', this.documentKeydownHandler);
		}
		if (this.input && this.inputKeydownHandler) {
			this.input.removeEventListener('keydown', this.inputKeydownHandler);
		}
		if (this.predictiveSearchResults && this.resultsKeydownHandler) {
			this.predictiveSearchResults.removeEventListener('keydown', this.resultsKeydownHandler);
		}
	}
}

// Define the custom element
customElements.define('predictive-search', PredictiveSearch);

// Fonction globale pour effacer la recherche
window.mmClearSearch = function() {
	const predictiveSearchElements = document.querySelectorAll('predictive-search');
	predictiveSearchElements.forEach(predictiveSearch => {
		if (predictiveSearch && typeof predictiveSearch.mmClearSearch === 'function') {
			predictiveSearch.mmClearSearch();
		}
	});
};

// Fonction globale pour fermer complètement la recherche prédictive
window.mmCloseSearch = function() {
	const predictiveSearchElements = document.querySelectorAll('predictive-search');
	predictiveSearchElements.forEach(predictiveSearch => {
		if (predictiveSearch && typeof predictiveSearch.closeSearch === 'function') {
			predictiveSearch.closeSearch();
			// Blur l'input pour fermer complètement
			const input = predictiveSearch.querySelector('input[type="search"]');
			if (input) {
				input.blur();
			}
		}
	});
};

// Utiliser event delegation pour gérer les clics sur les boutons de fermeture
// Cela fonctionne quelle que soit la structure DOM ou le moment d'initialisation
document.addEventListener('click', function(e) {
	// Trouver le bouton .mm-icon-search le plus proche
	const button = e.target.closest('predictive-search .mm-icon-search');
	if (!button) return;

	const closeIcon = button.querySelector('.mm-icon-close-input');
	// Seulement fermer si l'icône de fermeture est visible (recherche ouverte)
	if (closeIcon && window.getComputedStyle(closeIcon).display !== 'none') {
		e.stopPropagation();
		e.preventDefault();
		window.mmCloseSearch();
	}
});

// S'assurer que les SVGs ne bloquent pas les clics
document.addEventListener('DOMContentLoaded', function() {
	const searchIcons = document.querySelectorAll('.mm-icon-search-input, .mm-icon-close-input');
	searchIcons.forEach(function(svg) {
		svg.style.pointerEvents = 'none';
	});
});

// Fonction globale pour positionner la recherche
window.getPredictiveSearchPosition = function() {
	const searchInput = document.getElementById("Search");
	const predictiveSearchElements = document.querySelectorAll("#predictive-search");
	const predictiveSearchDefaultElements = document.querySelectorAll("#predictive-search-default");
	const header = document.querySelector(".mm-section-header");
	const isMobile = window.innerWidth <= 1400;

	console.log('🔍 Elements found:', {
		searchInput: !!searchInput,
		predictiveSearchCount: predictiveSearchElements.length,
		predictiveSearchDefaultCount: predictiveSearchDefaultElements.length,
		header: !!header,
		isMobile
	});

	if (!searchInput) {
		console.warn('❌ Search input not found');
		return;
	}

	let topPosition;

	if (isMobile) {
		// Mobile: position exactement sous le header
		if (header) {
			const headerRect = header.getBoundingClientRect();
			topPosition = headerRect.bottom;
			console.log('📱 Mobile mode - Header found:', {
				bottom: headerRect.bottom,
				height: headerRect.height,
				top: headerRect.top,
				calculatedTop: topPosition
			});
		} else {
			console.warn('❌ Header not found in mobile mode, using input position');
			const inputRect = searchInput.getBoundingClientRect();
			topPosition = inputRect.bottom;
		}
	} else {
		// Desktop: position 10px sous l'input
		const inputRect = searchInput.getBoundingClientRect();
		topPosition = inputRect.bottom + 10;

		// Fallback si l'input n'est pas encore positionné correctement
		if (topPosition < 60) {
			topPosition = 90;
		}
	}

	// Positionner TOUS les éléments predictive-search
	predictiveSearchElements.forEach(el => {
		el.style.top = `${topPosition}px`;
	});

	// Positionner TOUS les éléments predictive-search-default
	predictiveSearchDefaultElements.forEach(el => {
		el.style.top = `${topPosition}px`;
	});

	console.log('✅ Search positioned at:', topPosition, 'px');
};

// Appeler au chargement de la page
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', window.getPredictiveSearchPosition);
} else {
	window.getPredictiveSearchPosition();
}

// Appeler lors du resize (debounced)
let resizeTimeout;
window.addEventListener('resize', () => {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(window.getPredictiveSearchPosition, 150);
});